import { useObserver } from "mobx-react";
import React, { useContext, useEffect, useMemo } from "react";
import { Edit } from "@material-ui/icons";
import { Box, Button, Grid, IconButton, Paper, Typography } from "@material-ui/core";
import locale from "@bonliva-crm/locale";
import { useGetCustomerTreeLazyQuery } from "@bonliva-crm/graphql";
import { Date, Text } from "@common/formx";
import { SecondaryButton, Spinner } from "@components";
import { CustomerTreeSelect } from "@containers";
import { useNavManager, useUserProfile } from "@hooks";
import { useAddNewPriceGroup } from "./useAddNewPriceGroup";
import { PriceGroupFormLocation } from "../PriceGroupForm/PriceGroupFormLocation";
import { CustomerContractPriceListFormContext } from "../CustomerContractPriceListFormContext";
import { CustomerContractPriceRow } from "../../../config_editor/ContractsPriceTemplates/Search/components/CustomerContractPriceRow";
import { TravelZoneTable } from "./travel-zone-table";
import { Color, useSnackbarsPipeline } from "@bonliva-crm/ui";

export function PriceListMainForm() {
  const [showOrgTree, setShowOrgTree] = React.useState(false);

  const editorStore = useContext(CustomerContractPriceListFormContext);
  const snack = useSnackbarsPipeline();
  const { navManager } = useNavManager();
  const { add } = useAddNewPriceGroup();
  const { can } = useUserProfile();

  const [load, { data, loading }] = useGetCustomerTreeLazyQuery({
    variables: {
      customerContractId: editorStore.customerContractId
    },
    onCompleted: () => {
      setShowOrgTree(true);
    },
    onError: (err) => {
      snack.showError("Error loading customers");
    }
  });

  useEffect(() => {
    if (editorStore.isNew) {
      load();
    }
  }, [load, editorStore.isNew]);

  const customerTreeItems = useMemo(() => {
    if (!data?.customerContractById?.orgCustomerTree) {
      return [];
    }

    // open all groups by default
    return data.customerContractById.orgCustomerTree.map((x) => ({
      ...x,
      isGroupOpen: !x.ParentId
    }));
  }, [data]);

  const handleEdit = (priceGroupId) =>
    navManager.open(
      "price_group_form",
      new PriceGroupFormLocation(
        editorStore.customerContractId,
        editorStore.priceListId,
        priceGroupId,
        editorStore
      )
    );

  return useObserver(() => (
    <Grid container spacing={4}>
      <Grid item xs={12}>
        <Text label={locale.customerContractPriceLists.name} name="name" />
      </Grid>
      <Grid item xs={12}>
        {!showOrgTree && (
          <Box
            border={`1px solid ${Color.LightGray}`}
            display="flex"
            justifyContent="center"
            alignItems="center"
            borderRadius={4}
            p={4}
          >
            <Button variant="outlined" onClick={() => load()} disabled={loading}>
              {loading ? "Laddar.." : "Ladda kunder"}
              {loading && <Spinner size={16} style={{ marginLeft: 8 }} />}
            </Button>
          </Box>
        )}

        {showOrgTree && (
          <Box>
            <CustomerTreeSelect
              expanded
              multi
              skipChipDeleteIcon
              items={customerTreeItems}
              value={[...editorStore.form.values.customerIds]}
              onChange={(v) => editorStore.form.setValue("customerIds", v)}
            />
          </Box>
        )}
      </Grid>
      <Grid item container xs={12} spacing={4}>
        <Grid item container xs={6} spacing={4}>
          <Grid item xs={12}>
            <Date label={locale.customerContractPriceLists.startDate} name="startDate" />
          </Grid>
          <Grid item xs={12}>
            <Date label={locale.customerContractPriceLists.endDate} name="endDate" />
          </Grid>
          <Grid item xs={12}>
            <Text
              label={locale.administrativeFee}
              name="administrativeFee"
              TextFieldProps={{
                style: { textAlign: "right" },
                type: "number",
                InputProps: {
                  endAdornment: `%`
                }
              }}
            />
          </Grid>
        </Grid>

        <Grid item xs={6}>
          {editorStore.isNationalProcurement && (
            <TravelZoneTable items={editorStore.form.values.travelZones} />
          )}
        </Grid>
      </Grid>
      <Grid item xs={12} style={{ display: "flex" }}>
        <SecondaryButton
          style={{ marginLeft: "auto" }}
          onClick={() => add()}
          disabled={!can("customer-contract:edit:all")}
        >
          {locale.AddPriceBlock}
        </SecondaryButton>
      </Grid>
      <Grid item xs={12} style={{ marginTop: 16 }}>
        {editorStore.form.values.priceGroups.map((priceGroup, i) => {
          return (
            <Paper key={priceGroup._id}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: 10
                }}
              >
                <Typography>
                  {locale.PriceBlock}
                  {` `}
                  {i + 1}
                </Typography>
                <IconButton
                  title={locale.Edit}
                  onClick={() => handleEdit(priceGroup._id)}
                  disabled={!can("customer-contract:edit:all")}
                >
                  <Edit />
                </IconButton>
              </div>
              {priceGroup.professionsPrice?.map((p, i) => (
                <CustomerContractPriceRow
                  key={i}
                  row={{
                    ...p,
                    records: priceGroup.records,
                    highHolidays: priceGroup.highHolidays
                  }}
                />
              ))}
            </Paper>
          );
        })}
      </Grid>
    </Grid>
  ));
}
